import { Form, FormInstance, Upload } from "antd";
import { UploadFile } from "antd/es/upload";
import VLButton from "views/uikit/Buttons/VLButton";
import ImageUploadIcon from "assets/icons/ImageUploadIcon";
import { SelectedFileIcon } from "assets/icons/SelectedFileIcon";
import XIcon from "assets/icons/XIcon";
import styles from "./style.module.scss";

interface CloudFileUploadProps {
  form: FormInstance;
}

const CloudFileUpload = ({ form }: CloudFileUploadProps) => {
  const validateFiles = async (_: any, fileList: UploadFile[]) => {
    if (!fileList?.length) {
      return Promise.reject("Please upload DB.csv and Classes.json files");
    }

    const files = fileList.map((f) => f.name.toLowerCase());

    const hasDb = files.includes("db.csv");
    const hasClasses = files.includes("classes.json");
    const invalidFiles = files.filter(
      (name) => name !== "db.csv" && name !== "classes.json",
    );

    if (invalidFiles.length) {
      return Promise.reject("Only DB.csv and Classes.json are allowed");
    }

    if (files.length === 1) {
      if (!hasDb) return Promise.reject("DB.csv is required");
      if (!hasClasses) return Promise.reject("Classes.json is required");
    }

    if (files.length === 2) {
      if (files[0] === files[1]) {
        return Promise.reject("Cannot upload two files of the same type");
      }
      if (!hasDb || !hasClasses) {
        return Promise.reject("Both DB.csv and Classes.json are required");
      }
    }

    return Promise.resolve();
  };

  const removeFile = (uid: string) => {
    const newList = (form.getFieldValue("fileBrowser") || []).filter(
      (f: UploadFile) => f.uid !== uid,
    );

    form.setFieldsValue({
      fileBrowser: newList,
      files: newList.map((f: UploadFile) => f.originFileObj).filter(Boolean),
    });
  };

  const { Dragger } = Upload;

  return (
    <>
      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) => prev.fileBrowser !== curr.fileBrowser}
      >
        {({ getFieldValue }) => {
          const fileList: UploadFile[] = getFieldValue("fileBrowser") || [];
          const isUploadDisabled = fileList.length >= 2;

          return (
            <Form.Item
              name="fileBrowser"
              rules={[{ validator: validateFiles }]}
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList}
              className={styles.uploadButton}
            >
              <Dragger
                beforeUpload={() => false}
                accept=".csv,.json"
                multiple
                maxCount={2}
                showUploadList={false}
                disabled={isUploadDisabled}
                className={styles.cloudUploadContainer}
                onChange={(info) => {
                  form.setFieldsValue({
                    files: info.fileList
                      .map((f) => f.originFileObj)
                      .filter(Boolean),
                  });
                }}
              >
                <div className={styles.draggerContent}>
                  <ImageUploadIcon />

                  <div className={styles.textContainer}>
                    <div className={styles.titleText}>
                      Drag and drop files or click to upload
                    </div>
                    <div className={styles.subtitleText}>
                      Supported Files: DB.csv | Classes.json
                    </div>
                  </div>

                  <VLButton
                    text="Browse Files"
                    type="primary"
                    className={styles.browseButton}
                    disabled={isUploadDisabled}
                  />
                </div>
              </Dragger>
            </Form.Item>
          );
        }}
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) => prev.fileBrowser !== curr.fileBrowser}
      >
        {({ getFieldValue }) => {
          const fileList: UploadFile[] = getFieldValue("fileBrowser") || [];

          if (!fileList.length) return null;

          return (
            <div className={styles.fileListContainer}>
              {fileList.map((file) => (
                <div
                  key={file.uid}
                  className={`${styles.selectedFileList} ${styles.fileItem}`}
                >
                  <div className={styles.fileName}>
                    <SelectedFileIcon />
                    {file.name}

                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.uid);
                      }}
                      className={styles.removeIcon}
                    >
                      <XIcon />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        }}
      </Form.Item>
    </>
  );
};

export default CloudFileUpload;
